import { cn } from '@heroui/react'

import { type PanelRow } from '../../payload/panels'
import { n, num, ratePct, s } from '../format'

type PipelineProps = { rows: PanelRow[]; labelKey: string; valueKey: string }

type Flag = 'weak' | 'over' | 'broken' | null

// Every flagged state carries a glyph and a word as well as a colour.
const FLAGS: Record<Exclude<Flag, null>, { glyph: string; word: string }> = {
  weak: { glyph: '▼', word: 'weak' },
  over: { glyph: '▲', word: 'over 100%' },
  broken: { glyph: '⋯', word: 'no link' },
}

/**
 * Steps with the conversion INTO each step shown on the step itself, not on a
 * connector between two. Not a funnel: steps are not nested, so a later step
 * can exceed an earlier one and a rate over 100% is flagged, never clipped. A
 * link both sides declare unlinkable is drawn broken, with words, not a rate.
 */
const Pipeline = ({ rows, labelKey, valueKey }: PipelineProps) => (
  <div
    className="grid gap-2 overflow-x-auto"
    style={{
      gridTemplateColumns: `repeat(${rows.length}, minmax(120px, 1fr))`,
    }}
  >
    {rows.map((row, i) => {
      const prev = i ? rows[i - 1] : null
      const from = prev ? n(prev[valueKey]) : null
      const to = n(row[valueKey])
      const broken = Boolean(prev?.cannot_be_linked && row.cannot_be_linked)
      const rate =
        !prev || broken || from === null || from <= 0 ? null : to / from
      const flag: Flag = broken
        ? 'broken'
        : rate === null
          ? null
          : rate > 1
            ? 'over'
            : rate < 0.25
              ? 'weak'
              : null

      const rateText = broken
        ? FLAGS.broken.word
        : rate === null
          ? i === 0
            ? 'start'
            : ''
          : `${ratePct(rate)}${flag ? ` ${FLAGS[flag].glyph} ${FLAGS[flag].word}` : ''}`

      return (
        <div
          key={s(row[labelKey])}
          className={cn(
            'grid min-w-0 gap-1 border-l-2 pl-3',
            broken ? 'border-muted border-dashed' : 'border-border'
          )}
        >
          <div className="text-xl font-semibold tracking-tight tabular-nums">
            {num(to)}
          </div>
          <div className={cn('text-xs', flag ? 'text-flag' : 'text-muted')}>
            {rateText}
          </div>
          <div className="text-sm">{s(row[labelKey])}</div>
        </div>
      )
    })}
  </div>
)

export default Pipeline
